import { ActivatedRouteSnapshot, CanActivateFn, GuardResult, RouterStateSnapshot } from '@angular/router';
import { inject, Injector, runInInjectionContext } from '@angular/core';
import { concatMap, forkJoin, from, last, map, Observable, of, takeWhile } from 'rxjs';

function executeGuard(
  guard: CanActivateFn,
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<GuardResult> {
  const guardResult = guard(route, state);
  if (guardResult instanceof Observable) {
    return guardResult;
  } else if (guardResult instanceof Promise) {
    return from(guardResult);
  } else {
    return of(guardResult);
  }
}

/**
 * Chains multiple route guard functions and ensures they execute in a specific sequential order.
 *
 * @param {...(CanActivateFn | CanActivateFn[])} guards - Variable number of route guards or groups of route guards.
 * Each guard is a function or group of functions that determines whether a route can be activated.
 * @return {CanActivateFn} If a single guard is provided, returns a single guard function that executes the provided guards in order
 * and respects the result of each guard/group of guards to decide continuation or termination.
 */
export function guardsInOrder(...guards: (CanActivateFn | CanActivateFn[])[]): CanActivateFn {
  return (route, state) => {
    const injectionContext = inject(Injector);
    return from(guards).pipe(
      concatMap(guard => {
        return runInInjectionContext(injectionContext, () => {
          if (Array.isArray(guard)) {
            /**
             * Group of guards executed in parallel, the single result determines the final result
             * If any guard returns non-true, the result is the first non-true value
             */
            return forkJoin((guard as CanActivateFn[]).map(g => executeGuard(g, route, state))).pipe(
              map((results: GuardResult[]) => {
                return (results.find(res => res !== true) || true) as GuardResult;
              })
            );
          }
          return executeGuard(guard as CanActivateFn, route, state);
        });
      }),
      takeWhile(value => value === true, true),
      last()
    );
  };
}